import { useEffect, useState } from 'react';

const REVEAL_RANKS = [2, 3, 4] as const;

/**
 * 공개 시점(ms) — 1위 → CTA → (dim) 2·3·4위 → 가맹점
 */
const TIMELINE = {
  winner: 320,
  cta: 980,
  scrim: 1180,
  rankAt: [1380, 1680, 2120],
  restaurant: 2620,
} as const;

export interface RankingRevealState {
  visibleRanks: ReadonlySet<number>;
  showScrim: boolean;
  showWinner: boolean;
  showCta: boolean;
  showRestaurant: boolean;
}

function revealAll(setters: {
  setVisibleRanks: (v: ReadonlySet<number>) => void;
  setShowScrim: (v: boolean) => void;
  setShowWinner: (v: boolean) => void;
  setShowCta: (v: boolean) => void;
  setShowRestaurant: (v: boolean) => void;
}) {
  setters.setVisibleRanks(new Set([2, 3, 4]));
  setters.setShowScrim(true);
  setters.setShowWinner(true);
  setters.setShowCta(true);
  setters.setShowRestaurant(true);
}

/** 투표 결과 진입 시 1위→리워드 CTA→2·3·4위→가맹점 순으로 공개한다. */
export function useRankingReveal(enabled: boolean): RankingRevealState {
  const [visibleRanks, setVisibleRanks] = useState<ReadonlySet<number>>(() =>
    enabled ? new Set<number>() : new Set([2, 3, 4]),
  );
  const [showScrim, setShowScrim] = useState(!enabled);
  const [showWinner, setShowWinner] = useState(!enabled);
  const [showCta, setShowCta] = useState(!enabled);
  const [showRestaurant, setShowRestaurant] = useState(!enabled);

  useEffect(() => {
    const setters = {
      setVisibleRanks,
      setShowScrim,
      setShowWinner,
      setShowCta,
      setShowRestaurant,
    };

    if (!enabled) {
      revealAll(setters);
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      revealAll(setters);
      return;
    }

    const timers: number[] = [];

    timers.push(window.setTimeout(() => setShowWinner(true), TIMELINE.winner));
    timers.push(window.setTimeout(() => setShowCta(true), TIMELINE.cta));
    timers.push(window.setTimeout(() => setShowScrim(true), TIMELINE.scrim));

    REVEAL_RANKS.forEach((rank, index) => {
      timers.push(
        window.setTimeout(() => {
          setVisibleRanks((prev) => {
            const next = new Set(prev);
            next.add(rank);
            return next;
          });
        }, TIMELINE.rankAt[index]),
      );
    });

    timers.push(window.setTimeout(() => setShowRestaurant(true), TIMELINE.restaurant));

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [enabled]);

  return { visibleRanks, showScrim, showWinner, showCta, showRestaurant };
}
